import React, { useState, useEffect } from 'react';
import { X, Keyboard } from 'lucide-react';
import { useKeyboardShortcuts, ShortcutCategory } from '../../hooks/useKeyboardShortcuts';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { getShortcutsByCategory, formatShortcut, setModalOpen } = useKeyboardShortcuts();
  const [categories, setCategories] = useState<ShortcutCategory[]>([]);

  useEffect(() => {
    if (isOpen) {
      setCategories(getShortcutsByCategory());
      setModalOpen(true);
    } else {
      setModalOpen(false);
    }
  }, [isOpen, getShortcutsByCategory, setModalOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm !mt-0"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div 
        className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[95vh] overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcuts-modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <Keyboard className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            <h2 
              id="shortcuts-modal-title"
              className="text-xl font-semibold text-gray-900 dark:text-white"
            >
              Keyboard Shortcuts
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close keyboard shortcuts modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {categories.map((category) => (
              <div key={category.name} className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                  {category.name}
                </h3>
                <div className="space-y-3">
                  {category.shortcuts.map((shortcut, index) => (
                    <div 
                      key={`${category.name}-${index}`}
                      className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                        {shortcut.description}
                      </span>
                      <div className="flex items-center space-x-1 ml-4">
                        {formatShortcut(shortcut).split(' + ').map((key, keyIndex) => (
                          <React.Fragment key={keyIndex}>
                            {keyIndex > 0 && (
                              <span className="text-xs text-gray-400 dark:text-gray-500 mx-1">
                                +
                              </span>
                            )}
                            <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm">
                              {key}
                            </kbd>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer note */}
          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
              </div>
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-1">Tips:</p>
                <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                  <li>• Shortcuts are disabled when typing in input fields</li>
                  <li>• Press <kbd className="px-1 py-0.5 text-xs bg-blue-100 dark:bg-blue-800 rounded">?</kbd> anytime to show this help</li>
                  <li>• Press <kbd className="px-1 py-0.5 text-xs bg-blue-100 dark:bg-blue-800 rounded">Esc</kbd> to close modals and dialogs</li>
                  <li>• Use <kbd className="px-1 py-0.5 text-xs bg-blue-100 dark:bg-blue-800 rounded">Tab</kbd> and <kbd className="px-1 py-0.5 text-xs bg-blue-100 dark:bg-blue-800 rounded">Shift+Tab</kbd> for keyboard navigation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Global keyboard shortcuts modal manager
export const KeyboardShortcutsModalManager: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleShowModal = () => setIsOpen(true);
    const handleCloseModal = () => setIsOpen(false);

    window.addEventListener('show-shortcuts-modal', handleShowModal);
    window.addEventListener('close-modal', handleCloseModal);

    return () => {
      window.removeEventListener('show-shortcuts-modal', handleShowModal);
      window.removeEventListener('close-modal', handleCloseModal);
    };
  }, []);

  return (
    <KeyboardShortcutsModal 
      isOpen={isOpen} 
      onClose={() => setIsOpen(false)} 
    />
  );
};

export default KeyboardShortcutsModal;